/**
 * L'écran de consultation du PARC MATÉRIEL.
 *
 * *Chris, 20/08/2026 : « une liste des matériels sera donc aussi consultable sur l'appli planif
 * dans un menu matériel ? », « il me faut tous les champs de la fiche matériel y compris les
 * photos », « je veux tout afficher sans exception, donc tu ne décides pas pour moi de ce qu'il
 * faut rapatrier ».*
 *
 * ## Rien ne se modifie ici, et c'est la règle qui gouverne tout l'écran
 *
 * **archive-roy est le référentiel maître.** Une correction se fait là-bas et apparaît ici au
 * rafraîchissement suivant. Il n'y a donc ni cellule éditable, ni réimport de classeur, ni
 * suppression, ni ajout — pas parce que ce serait difficile, mais parce que deux endroits pour
 * écrire la même fiche est exactement ce que ce produit refuse.
 *
 * C'est la différence avec l'écran des articles, qui se ressemble par ailleurs : le catalogue
 * d'articles vit ICI, le parc matériel vit AILLEURS.
 *
 * ## Le QR est fabriqué par Planif, à partir du code ERP
 *
 * *« Je pensais que tu recréerais un qr code dans planif à partir du code erp, je ne vois pas
 * pourquoi tu aurais besoin du qr code de archive-roy, c'est une grosse source d'embrouille. »*
 *
 * Il encode `planif-roy.app/materiel/<code ERP>` : scané, il ouvre **cette** fiche, pas celle du
 * référentiel documentaire où un mécano n'a pas de compte. Une machine, une étiquette — et
 * l'adresse désigne l'OBJET, jamais une fonction, pour que le module SAV à venir et tout ce qui
 * suivra partent de la même étiquette collée aujourd'hui.
 *
 * ## Le parc ENTIER, sorties comprises
 *
 * Une machine vendue ou passée « HS » reste affichée, avec son état. Refuser sa fiche devant une
 * étiquette parfaitement lisible se lirait comme une panne — et c'est justement l'engin qui ne va
 * pas qu'un mécano scanne. Les listes de CHOIX, elles, continuent de ne proposer que les
 * affectables : c'est un autre écran et un autre besoin.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import {
  adresseEncodeeDansLeQrMateriel,
  adresseEtiquettesMateriel,
  adresseQrMateriel,
  chargerParc,
  chargerPhotosMateriel,
} from "./api";
import type { FicheParc } from "./api";

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; materiels: readonly FicheParc[]; baseQr: string }
  | { phase: "erreur"; raison: string };

/**
 * Les colonnes de la fiche, **dans l'ordre où le référentiel les rend**.
 *
 * Cet ordre n'est pas un hasard qu'on aurait recopié : c'est celui de l'écran du parc dans
 * archive-roy, donc celui dans lequel quelqu'un qui connaît le référentiel s'attend à les lire.
 * Les réarranger « pour mieux faire » obligerait à traduire d'un écran à l'autre.
 *
 * `largeur` est en caractères, **remesurée sur les 227 machines du parc réel le 27/08/2026** : le
 * plus long contenu de la colonne, ou son en-tête s'il est plus long.
 *
 * *Chris, 27/08/2026 : « le tableau dans le menu parc matériel est illisible ».* Ces largeurs
 * étaient posées en `max-width` sur les cellules du CORPS, là où la mise en page `fixed` du
 * tableau ne regarde que la première rangée ou le `colgroup` : elles n'ont donc **jamais** pris
 * effet. Les 29 colonnes se partageaient la largeur de la fenêtre à parts égales — environ trois
 * caractères chacune — et `text-overflow: ellipsis` coupait tout. Elles vivent maintenant dans un
 * `colgroup`, comme la grille du déboursé.
 *
 * Trois colonnes gardent un plafond volontaire : « Description » (61 caractères au maximum, 46 au
 * 95e centile), « N° de série » (24 pour 17) et « État parc » (21 pour 8). Une seule machine ne
 * doit pas décider de la largeur d'une colonne qu'on lit sur deux cents lignes — le contenu
 * entier reste dans la fiche, qu'un clic sur le code ouvre, et dans l'infobulle de la cellule.
 */
const COLONNES: readonly {
  readonly champ: keyof FicheParc;
  readonly entete: string;
  readonly largeur: number;
  readonly genre?: "booleen" | "nombre";
}[] = [
  { champ: "description", entete: "Description", largeur: 40 },
  { champ: "generique", entete: "Générique", largeur: 12 },
  { champ: "type", entete: "Type", largeur: 32 },
  { champ: "immatriculation", entete: "Immatriculation", largeur: 12 },
  { champ: "numSerie", entete: "N° de série", largeur: 14 },
  { champ: "fournisseur", entete: "Fournisseur", largeur: 10 },
  { champ: "categorie", entete: "Catégorie", largeur: 14 },
  { champ: "codeCategorie", entete: "Code catégorie", largeur: 10 },
  { champ: "sousCategorie", entete: "Sous-catégorie", largeur: 22 },
  { champ: "societe", entete: "Société", largeur: 12 },
  { champ: "societeProprietaire", entete: "Société propriétaire", largeur: 17 },
  { champ: "poids", entete: "Poids", largeur: 6 },
  { champ: "essieux", entete: "Essieux", largeur: 9 },
  { champ: "affectationVehicule", entete: "Affectation", largeur: 25 },
  { champ: "assurance", entete: "Assurance", largeur: 8, genre: "booleen" },
  { champ: "bdm", entete: "BDM", largeur: 5, genre: "booleen" },
  { champ: "cbImmo", entete: "CB / Immo", largeur: 9 },
  { champ: "modeFinancement", entete: "Financement", largeur: 10 },
  { champ: "valeurAchat", entete: "Valeur d'achat", largeur: 10, genre: "nombre" },
  { champ: "dateEntree", entete: "Date d'entrée", largeur: 10 },
  { champ: "dateSortie", entete: "Date de sortie", largeur: 10 },
  { champ: "dateRachat", entete: "Date de rachat", largeur: 9 },
  { champ: "etatAchat", entete: "État achat", largeur: 8 },
  { champ: "etatParc", entete: "État parc", largeur: 12 },
  { champ: "statut", entete: "Statut", largeur: 7 },
  { champ: "actif", entete: "Actif", largeur: 6, genre: "booleen" },
];

/**
 * La grille du tableau, posée en `colgroup`.
 *
 * C'est la seule façon de fixer une largeur ici : le tableau est en `table-layout: fixed`
 * (idiome de `table.ressources`), et cette mise en page ne lit les largeurs que sur la première
 * rangée ou sur le `colgroup`. Une `max-width` sur une cellule du corps ne fait rien du tout.
 *
 * `0.7rem` s'ajoute à chaque largeur de texte : en `fixed`, la largeur d'un `<col>` est celle de
 * la BORDURE de la colonne, donc les deux `0.35rem` de marge intérieure des cellules mangeraient
 * autant de caractères que ce qu'on a mesuré.
 */
function ColonnesDuParc(): JSX.Element {
  return (
    <colgroup>
      <col style={{ width: "calc(12ch + 0.7rem)" }} />
      <col style={{ width: "3.2rem" }} />
      {COLONNES.map((c) => (
        <col key={c.champ} style={{ width: `calc(${String(c.largeur)}ch + 0.7rem)` }} />
      ))}
      <col style={{ width: "calc(6ch + 0.7rem)" }} />
    </colgroup>
  );
}

/** Combien de lignes s'affichent avant qu'on demande d'affiner. */
const LIGNES_AFFICHEES = 100;

function normaliser(texte: string): string {
  return texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/gu, "")
    .toLowerCase();
}

/**
 * Les machines qui répondent à une recherche : par code, immatriculation, description ou type.
 *
 * Quatre champs et non un seul, parce qu'on cherche une machine par ce qu'on a sous les yeux : le
 * code sur l'étiquette, la plaque sur le pare-chocs, ou « niveleuse » quand on ne sait ni l'un ni
 * l'autre. Chaque mot doit se retrouver, dans n'importe lequel des quatre et dans n'importe quel
 * ordre.
 */
export function chercherMateriels(
  materiels: readonly FicheParc[],
  recherche: string,
): readonly FicheParc[] {
  const mots = normaliser(recherche)
    .split(/\s+/u)
    .filter((m) => m !== "");
  if (mots.length === 0) return materiels;
  return materiels.filter((m) => {
    const dans = normaliser(
      [m.codeErp, m.immatriculation, m.description, m.type].filter((x) => x !== null).join(" "),
    );
    return mots.every((mot) => dans.includes(mot));
  });
}

/** `null` ne s'affiche pas comme « non » : une case vide du référentiel n'est pas une réponse. */
function afficher(valeur: unknown): string {
  if (valeur === null || valeur === undefined || valeur === "") return "—";
  if (valeur === true) return "oui";
  if (valeur === false) return "non";
  return String(valeur);
}

/** Une machine n'est plus au parc : fiche inactive, statut sorti, ou date de sortie passée. */
function horsParc(m: FicheParc): boolean {
  const jour = new Date().toISOString().slice(0, 10);
  if (m.actif !== true) return true;
  if (m.statut?.toLowerCase() === "out") return true;
  return m.dateSortie !== null && m.dateSortie <= jour;
}

/** Le QR en grand, par-dessus l'écran — même geste que pour les articles. */
function QrAgrandi({
  materiel,
  baseQr,
  onFermer,
}: {
  materiel: FicheParc;
  baseQr: string;
  onFermer: () => void;
}): JSX.Element {
  useEffect(() => {
    const surTouche = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onFermer();
    };
    window.addEventListener("keydown", surTouche);
    return () => {
      window.removeEventListener("keydown", surTouche);
    };
  }, [onFermer]);

  return (
    <div
      className="qr-calque"
      role="dialog"
      aria-modal="true"
      aria-label={`QR code de ${materiel.codeErp}`}
      onClick={onFermer}
    >
      <div
        className="qr-calque__cadre"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <img
          className="qr-calque__image"
          src={adresseQrMateriel(materiel.id, baseQr)}
          alt={`QR code de ${materiel.codeErp}`}
        />
        <p className="qr-calque__code">{materiel.codeErp}</p>
        <p className="qr-calque__libelle">
          {[materiel.type, materiel.description, materiel.immatriculation]
            .filter((x) => x !== null)
            .join(" · ")}
        </p>
        <p className="qr-calque__contenu">
          {adresseEncodeeDansLeQrMateriel(baseQr, materiel.codeErp)}
        </p>
        <p className="etat">
          <a
            href={adresseEtiquettesMateriel([materiel.codeErp])}
            target="_blank"
            rel="noreferrer"
          >
            Imprimer l'étiquette 6 × 6 cm
          </a>
          {" · "}
          <button type="button" className="lien" onClick={onFermer}>
            Fermer
          </button>
        </p>
      </div>
    </div>
  );
}

/**
 * Les deux photos d'engin, chargées à l'ouverture de la fiche.
 *
 * Séparées de la liste parce que leur adresse est signée et ne vit que quinze minutes : les
 * transporter avec les 227 fiches servirait parfois des vignettes cassées.
 */
function Photos({ materiel }: { materiel: FicheParc }): JSX.Element {
  const [etat, setEtat] = useState<
    | { phase: "chargement" }
    | { phase: "chargees"; photos: Record<string, string | null>; message: string | null }
    | { phase: "erreur"; raison: string }
  >({ phase: "chargement" });

  useEffect(() => {
    let abandonne = false;
    setEtat({ phase: "chargement" });
    chargerPhotosMateriel(materiel.id)
      .then(({ photos, message }) => {
        if (!abandonne) setEtat({ phase: "chargees", photos, message });
      })
      .catch((e: unknown) => {
        if (!abandonne) {
          setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
        }
      });
    return () => {
      abandonne = true;
    };
  }, [materiel.id]);

  if (etat.phase === "chargement") return <p className="note">Chargement des photos…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const adresses = Object.values(etat.photos).filter((v): v is string => v !== null);

  /*
    **On demande TOUJOURS, on ne se fie pas au drapeau de la liste.**

    *Chris, 20/08/2026 : « si je change une photo dans archive-roy, on est d'accord que cela se met
    à jour dans Planif ».* Oui — mais cet écran s'arrêtait avant de demander quand `aPhoto1` et
    `aPhoto2` étaient faux, et ces deux drapeaux viennent de la liste du parc, gardée dix minutes.
    Une photo ajoutée côté référentiel donnait donc « aucune photo » pendant dix minutes, ce qui
    est un état FAUX affiché avec aplomb.

    Demander coûte une requête au moment où l'on ouvre une fiche, c'est-à-dire rien. Les drapeaux
    restent utiles à la LISTE, où ils évitent 227 appels pour afficher une colonne.
  */
  if (adresses.length === 0) {
    return (
      <p className="note">
        {etat.message ??
          "Aucune photo au référentiel. Les photos d'engin se déposent dans archive-roy — cet " +
            "écran les affiche, il ne les modifie pas."}
      </p>
    );
  }

  return (
    <>
      {etat.message !== null && <p className="alerte">{etat.message}</p>}
      <div className="materiel-photos">
        {adresses.map((adresse, rang) => (
          <a key={adresse} href={adresse} target="_blank" rel="noreferrer">
            <img src={adresse} alt={`Photo ${String(rang + 1)} de ${materiel.codeErp}`} />
          </a>
        ))}
      </div>
    </>
  );
}

/** La fiche d'une machine : ses 29 champs, ses photos, son QR. En lecture seule. */
function Fiche({
  materiel,
  baseQr,
  onAgrandirQr,
  onFermer,
}: {
  materiel: FicheParc;
  baseQr: string;
  onAgrandirQr: () => void;
  onFermer: () => void;
}): JSX.Element {
  return (
    <section className="article-fiche">
      <h3>
        <span className="code">{materiel.codeErp}</span> — {materiel.description ?? "sans libellé"}
        {horsParc(materiel) && <span className="materiel-hors-parc">hors parc</span>}
        {" · "}
        <button type="button" className="lien" onClick={onFermer}>
          fermer
        </button>
      </h3>

      <div className="article-fiche__corps">
        <div className="article-fiche__cote">
          <div className="article-groupe">
            <h4>QR code</h4>
            <button
              type="button"
              className="article-qr"
              title="Agrandir pour le scaner"
              onClick={onAgrandirQr}
            >
              <img
                src={adresseQrMateriel(materiel.id, baseQr)}
                alt={`QR code de ${materiel.codeErp}`}
              />
            </button>
            {/* Ce que le QR contient, en clair : le seul moyen de vérifier ce qu'une étiquette
                portera avant d'en imprimer deux cents. */}
            <p className="article-qr__contenu">
              {adresseEncodeeDansLeQrMateriel(baseQr, materiel.codeErp)}
            </p>
            <p className="etat">
              <button type="button" className="lien" onClick={onAgrandirQr}>
                Agrandir pour scaner
              </button>
              {" · "}
              <a
                href={adresseEtiquettesMateriel([materiel.codeErp])}
                target="_blank"
                rel="noreferrer"
              >
                Imprimer son étiquette
              </a>
            </p>
            <p className="note">
              Étiquette de 6 × 6 cm — imprimer à 100 %, sans mise à l'échelle. Scaner ce code ouvre
              cette fiche.
            </p>
          </div>

          <div className="article-groupe">
            <h4>Photos</h4>
            <Photos materiel={materiel} />
          </div>
        </div>

        <div className="article-fiche__champs">
          <div className="article-groupe">
            <h4>Fiche du référentiel</h4>
            <table className="article-champs">
              <tbody>
                {COLONNES.map((c) => (
                  <tr key={c.champ}>
                    <th>{c.entete}</th>
                    <td>{afficher(materiel[c.champ])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Materiel({ codeInitial }: { codeInitial?: string } = {}): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [recherche, setRecherche] = useState(codeInitial ?? "");
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [qrEnGrand, setQrEnGrand] = useState<string | null>(null);
  /** On arrive d'un scan : l'écran ne montre QUE cette machine. */
  const [modeScan, setModeScan] = useState(codeInitial !== undefined);

  /**
   * Quitter la page d'arrivée d'un scan, **en refermant la fiche**.
   *
   * *Chris, 20/08/2026 : « j'ai l'impression qu'il y a deux fois la même fiche ».* La fiche de la
   * machine scanée restait ouverte au-dessus du tableau : on voyait donc la fiche, puis la liste
   * qui contient la même ligne. Deux blocs pour un seul objet se lisent comme un doublon — et le
   * bouton disait « fermer » sans rien fermer.
   */
  const quitterLeScan = (): void => {
    setModeScan(false);
    setOuvert(null);
  };
  const premierRendu = useRef(true);

  useEffect(() => {
    let abandonne = false;
    chargerParc()
      .then(({ materiels, baseQr }) => {
        if (!abandonne) setEtat({ phase: "chargee", materiels, baseQr });
      })
      .catch((e: unknown) => {
        if (!abandonne) {
          setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
        }
      });
    return () => {
      abandonne = true;
    };
  }, []);

  const materiels = etat.phase === "chargee" ? etat.materiels : [];
  const baseQr = etat.phase === "chargee" ? etat.baseQr : "";

  /**
   * Ouvrir la fiche que l'adresse demande, dès que le parc est là.
   *
   * Les codes se comparent **sans leurs séparateurs** : une douchette qui rend « 040201102 » doit
   * trouver « 04-02-01-102 », comme le fait déjà le serveur.
   */
  const scanee = useMemo(() => {
    if (codeInitial === undefined) return undefined;
    const compacter = (c: string): string => c.replace(/[^0-9a-z]/giu, "").toLowerCase();
    const cherche = compacter(codeInitial);
    return materiels.find((m) => compacter(m.codeErp) === cherche);
  }, [codeInitial, materiels]);

  useEffect(() => {
    if (scanee !== undefined && premierRendu.current) {
      premierRendu.current = false;
      setOuvert(scanee.id);
    }
  }, [scanee]);

  const trouves = useMemo(() => chercherMateriels(materiels, recherche), [materiels, recherche]);

  if (etat.phase === "chargement") return <p>Chargement du parc matériel…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const materielDuQr = materiels.find((m) => m.id === qrEnGrand);

  /* La page d'arrivée d'un scan : la fiche, seule. */
  if (modeScan) {
    return (
      <section className="referentiel articles articles--scan">
        {scanee === undefined ? (
          <div className="articles__amorce">
            <p>
              <strong>Aucune machine ne porte le code « {codeInitial} ».</strong> L'étiquette
              désigne une machine absente du référentiel — code renuméroté dans l'ERP, ou étiquette
              d'un autre parc.
            </p>
            <button
              type="button"
              className="lien"
              onClick={quitterLeScan}
            >
              Voir tout le parc
            </button>
          </div>
        ) : (
          <>
            <p className="etat">
              <button
                type="button"
                className="lien"
                onClick={quitterLeScan}
              >
                Voir tout le parc
              </button>
            </p>
            <Fiche
              materiel={scanee}
              baseQr={baseQr}
              onAgrandirQr={() => {
                setQrEnGrand(scanee.id);
              }}
              onFermer={quitterLeScan}
            />
            {materielDuQr !== undefined && (
              <QrAgrandi
                materiel={materielDuQr}
                baseQr={baseQr}
                onFermer={() => {
                  setQrEnGrand(null);
                }}
              />
            )}
          </>
        )}
      </section>
    );
  }

  const affiches = trouves.slice(0, LIGNES_AFFICHEES);
  const materiel = materiels.find((m) => m.id === ouvert);

  return (
    <section className="referentiel articles">
      <h2>
        Parc matériel — {materiels.length} machine(s)
        {recherche.trim() !== "" && ` · ${trouves.length} trouvée(s)`}
      </h2>

      <p className="note">
        Ce parc est lu dans <strong>archive-roy</strong>, le référentiel maître : rien ne se
        modifie ici, une correction se fait là-bas et apparaît au rafraîchissement suivant. Les
        étiquettes QR, elles, sont fabriquées par cette application à partir du code ERP — scaner
        l'une d'elles ouvre la fiche ci-dessous, et non le référentiel documentaire.
      </p>

      <p className="etat">
        <a href={adresseEtiquettesMateriel()} target="_blank" rel="noreferrer">
          Imprimer toutes les étiquettes
        </a>
        {" · "}
        <label>
          Chercher un code, une immatriculation, un libellé{" "}
          <input
            className="saisie saisie--texte"
            value={recherche}
            placeholder="04-02-01-102, EP 024 YB, niveleuse…"
            onChange={(e) => {
              setRecherche(e.target.value);
            }}
          />
        </label>
      </p>

      {materiel !== undefined && (
        <Fiche
          materiel={materiel}
          baseQr={baseQr}
          onAgrandirQr={() => {
            setQrEnGrand(materiel.id);
          }}
          onFermer={() => {
            setOuvert(null);
          }}
        />
      )}

      {materielDuQr !== undefined && (
        <QrAgrandi
          materiel={materielDuQr}
          baseQr={baseQr}
          onFermer={() => {
            setQrEnGrand(null);
          }}
        />
      )}

      <div className="articles__defilement">
        <table className="ressources articles__table materiel__table">
          <ColonnesDuParc />
          <thead>
            <tr>
              <th className="articles__code">Code ERP</th>
              <th>QR</th>
              {COLONNES.map((c) => (
                <th key={c.champ}>{c.entete}</th>
              ))}
              <th>Photos</th>
            </tr>
          </thead>
          <tbody>
            {affiches.map((m) => (
              <tr key={m.id} className={horsParc(m) ? "retiree" : undefined}>
                <th className="articles__code">
                  <button
                    type="button"
                    className="lien code"
                    onClick={() => {
                      setOuvert(m.id === ouvert ? null : m.id);
                    }}
                  >
                    {m.codeErp}
                  </button>
                </th>
                <td>
                  <button
                    type="button"
                    className="articles__qr"
                    title={`Agrandir le QR code de ${m.codeErp} pour le scaner`}
                    onClick={() => {
                      setQrEnGrand(m.id);
                    }}
                  >
                    <img
                      src={adresseQrMateriel(m.id, baseQr)}
                      alt={`QR code de ${m.codeErp}`}
                      loading="lazy"
                    />
                  </button>
                </td>
                {COLONNES.map((c) => (
                  <td
                    key={c.champ}
                    className={c.genre === "nombre" ? "col-nombre" : undefined}
                    /* Ce que la colonne coupe reste lisible au survol : la largeur est
                       mesurée sur le 95e centile, pas sur le contenu le plus long. */
                    title={afficher(m[c.champ])}
                  >
                    {afficher(m[c.champ])}
                  </td>
                ))}
                <td className="articles__photos">
                  {m.aPhoto1 ? "1" : "·"} {m.aPhoto2 ? "2" : "·"}
                </td>
              </tr>
            ))}
            {affiches.length === 0 && (
              <tr>
                <td colSpan={COLONNES.length + 3} className="vide">
                  {materiels.length === 0
                    ? "Le référentiel n'a rendu aucune machine."
                    : "Aucune machine ne correspond à cette recherche."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {trouves.length > affiches.length && (
        <p className="note">
          {affiches.length} lignes affichées sur {trouves.length} : affiner la recherche pour voir
          les suivantes.
        </p>
      )}
    </section>
  );
}
